import React, { useState, useContext } from "react";
import TenantList from "./TenantList";
import CreateTenantForm from "./CreateTenantForm";
import EditTenantForm from "./EditTenantPage";
import { AuthContext } from "../../contexts/AuthContext";

const TenantsPage = () => {
  const [activeTab, setActiveTab] = useState("list");
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const { user } = useContext(AuthContext);

  const handleEditClick = (tenant) => {
    setSelectedTenant(tenant);
    setActiveTab("edit");
  };

  const handleDoneEditing = () => {
    setSelectedTenant(null);
    setActiveTab("list");
    setRefreshTrigger((prev) => prev + 1);
  };

  const handleTenantCreated = () => {
    setActiveTab("list");
    setRefreshTrigger((prev) => prev + 1);
  };

  // Check if user is primary tenant admin
  if (!user?.is_primary || user?.role !== "admin") {
    return (
      <div className="container mt-4">
        <div className="alert alert-danger" role="alert">
          <i className="bi bi-shield-exclamation"></i>
          <strong> Access Denied</strong>
          <br />
          This section is restricted to primary tenant administrators only.
        </div>
      </div>
    );
  }

  return (
    <div className="container mt-3">
      <div className="row mb-4">
        <div className="col">
          <h2>Tenant Management</h2>
          <p className="text-muted">
            Manage all tenants in the system. Create, edit, and control access
            for tenants.
          </p>
        </div>
      </div>

      {/* PHI Notice */}
      <div
        className="alert alert-warning d-flex align-items-center mb-4"
        role="alert"
      >
        <i className="bi bi-shield-exclamation fs-4 me-3"></i>
        <div>
          <strong>Protected Health Information (PHI):</strong> This section may
          contain sensitive tenant data. All access and modifications are logged
          for HIPAA compliance. Only primary tenant administrators have access.
        </div>
      </div>

      {/* Tabs */}
      <ul className="nav nav-tabs mb-3">
        <li className="nav-item">
          <button
            className={`nav-link ${activeTab === "list" ? "active" : ""}`}
            onClick={() => {
              setActiveTab("list");
              setSelectedTenant(null);
            }}
          >
            <i className="bi bi-building"></i> All Tenants
          </button>
        </li>
        <li className="nav-item">
          <button
            className={`nav-link ${activeTab === "create" ? "active" : ""}`}
            onClick={() => {
              setActiveTab("create");
              setSelectedTenant(null);
            }}
          >
            <i className="bi bi-plus-circle"></i> Create Tenant
          </button>
        </li>
        {selectedTenant && (
          <li className="nav-item">
            <button
              className={`nav-link ${activeTab === "edit" ? "active" : ""}`}
              onClick={() => setActiveTab("edit")}
            >
              <i className="bi bi-pencil"></i> Edit:{" "}
              {selectedTenant.tenant_name}
            </button>
          </li>
        )}
      </ul>

      {/* Content */}
      <div className="tab-content">
        {activeTab === "list" && (
          <TenantList
            onEditTenant={handleEditClick}
            refreshTrigger={refreshTrigger}
          />
        )}

        {activeTab === "create" && (
          <>
            <h4>Create New Tenant</h4>
            <CreateTenantForm onCreated={handleTenantCreated} />
          </>
        )}

        {activeTab === "edit" && selectedTenant && (
          <EditTenantForm tenant={selectedTenant} onDone={handleDoneEditing} />
        )}
      </div>

      {/* HIPAA Compliance Footer */}
      <div className="mt-5 p-3 bg-light rounded">
        <h6 className="text-muted">
          <i className="bi bi-shield-check"></i> HIPAA Compliance & Security
        </h6>
        <small className="text-muted">
          <ul className="mb-0">
            <li>All tenant data is encrypted at rest and in transit</li>
            <li>
              Access to tenant management is restricted to primary tenant
              administrators
            </li>
            <li>All actions are logged with complete audit trails</li>
            <li>
              Tenants can be deactivated to revoke access while preserving data
            </li>
            <li>
              Primary tenant cannot be deactivated to maintain system integrity
            </li>
            <li>
              Each customer automatically creates an inactive tenant for future
              activation
            </li>
          </ul>
        </small>
      </div>
    </div>
  );
};

export default TenantsPage;
